import { GenericUtils } from '@unpispas/upp-defs';

import { BaseObject } from './model/base';
import { DataObject } from './model/base';
import { dataService } from './data';

import { PrintDevice } from './model/objects/printer';
import { Place } from './model/objects/place';
import { QrScan } from './model/objects/qrscan';

/**
 * @interface _Event
 * Base structure for all event data.
 */
interface _Event {
    created: Date | null,
    target: string | null,
};

/**
 * @abstract
 * @class RegistryEvent
 * Abstract base class for events. Defines common behaviors and structure for event objects.
 */
abstract class RegistryEvent extends DataObject {
    constructor(data: dataService){
        super('EVENT', null, data, {});
        this._isLoaded = true;
    }

    /****************************/
    /* DATA OBJECT ABSTRACTS    */
    /****************************/

    /**
     * Copies the current event into a new instance.
     * @param store - Optional storage for copied DataObjects.
     * @returns A new instance of the event.
     */    
    Copy(store: Array<DataObject> = []): RegistryEvent {
        return this._Copy(store) as RegistryEvent;
    }

    /**
     * Override setter for `Data`. No operation is performed.
     * @param value - Input data.
     */    
    set Data(value: any){
        void value; /* nothing to do */
    }
    /**
     * Override setter for `Info`. No operation is performed.
     * @param value - Input info.
     */    
    set Info(value: any){
        void value; /* nothing to do */    
    }

    /**
     * Override handler for updates. No operation is performed.
     * @param info - Input info for updates.
     */    
    protected _OnUpdate(info: any){
        void info; /* nothing to do */        
    }

    /****************************/
    /* SERVER NOTIFICATION      */
    /****************************/

    /**
     * Sends a notification if the event is ready for notification.
     */    
    Notify(): void{
        if (this.ToNotify){
            this.DoCommit(true);
        }
        else {
            console.warn("[NOTIFY] Event not ready for notification!")
        }
    }
}

/**************************************/
/* OPEN DRAWER EVENT                  */
/**************************************/

/**
 * @interface _DrawerEventData
 * Structure for Drawer Event-specific data.
 */
interface _DrawerEventData extends _Event {
    printer: number | null;
}

/**
 * @class DrawerEvent
 * Represents a drawer opening event.
 */
export class DrawerEvent extends RegistryEvent {
    protected _event: _DrawerEventData = {
        created: null,
        target: null,
        printer: null
    };

    constructor(data: dataService){
        super(data);

        this._event.created = new Date();
        this._event.target = 'DRAWER';
    }    

    /**
     * Returns event-specific information.
     */    
    override get Info(){
        return this._event;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    /**
     * Gets the associated printer device.
     */    
    get printer(): PrintDevice | null {
        if (this._children['printer'] instanceof PrintDevice){
            return this._children['printer'] as PrintDevice;
        }
        return null;
    }

    /**
     * Sets the associated printer device.
     * @param value - The printer device to associate.
     */    
    set printer(value: PrintDevice){
        if (this.SetChild('printer', value, 'printer')){
            this.ToNotify = true;
        }
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    /**
     * Returns the changes to be committed for the event.
     */    
    protected get Change() {
        return {
            created: this._event.created ? GenericUtils.dateStrToMysql(this._event.created) : null,
            target: this._event.target,
            printer: this._event.printer
        };
    }

    /**
     * Returns the dependencies for this event.
     */    
    protected get Depend() {
        return {
            printer: { item: this.printer as BaseObject, relation_info: { to: null, by: 'printer' } }         // no relation to this in this[by -> 'printer']
        };
    }

    /**
     * Returns the child dependencies of this event.
     */    
    protected get Children(){
        return [ /* empty */ ];
    }
}

/**************************************/
/* USER LOGIN/LOGOUT EVENT            */
/**************************************/

/**
 * @interface _LoginEventData
 * Structure for Login Event-specific data.
 */
export interface _LoginEventData extends _Event {
    place: number | null,
    login: boolean | null
}

/**
 * @class LoginEvent
 * Represents an user login event.
 */
export class LoginEvent extends RegistryEvent {
    protected _event: _LoginEventData = {
        created: null,
        target: null,
        place: null,
        login: null,
    };

    constructor(data: dataService){
        super(data);

        this._event.created = new Date();
        this._event.target = 'LOGIN';
    }    

    /**
     * Returns event-specific information.
     */      
    override get Info(){
        return this._event;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    /**
     * Determines whether the event is ready for notification.
     * Uses _event.place (payload) because AddRelationInfo sets it to null when Place has no objid.
     * @returns `true` if all required fields are populated, otherwise `false`.
     */    
    private get _isReady(){
        if (!this._event.place){
            return false;
        }
        if (this.login === null){
            return false;
        }
        return true;
    }

    /**
     * Gets the place associated with the login event.
     * @returns The `Place` object or `null` if not set.
     */    
    get place(): Place | null {
        if (this._children['place'] instanceof Place){
            return this._children['place'] as Place;
        }
        return null;
    }

    /**
     * Sets the place associated with the login event.
     * Automatically updates the `ToNotify` flag if the event is ready.
     * @param value - The `Place` object to associate (or null for logout without place).
     */    
    set place(value: Place | null){
        if (this.SetChild('place', value, 'place')){
            this.ToNotify = this._isReady;
        }
    }

    /**
     * Gets the login status of the event.
     * @returns `true` for login events, `false` for logout events.
     */    
    get login(): boolean {
        return this._event.login || false;
    }

    /**
     * Sets the login status of the event.
     * Automatically updates the `ToNotify` flag if the event is ready.
     * @param value - `true` for login, `false` for logout.
     */    
    set login(value: boolean) {
        if (this.patchValue(this._event, 'login', value)){
            this.ToNotify = this._isReady;
        }        
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    /**
     * Returns the changes to be committed for the event.
     */       
    protected get Change() {
        return {
            created: this._event.created ? GenericUtils.dateStrToMysql(this._event.created) : null,
            target: this._event.target,
            place: this._event.place,
            login: this._event.login ? '1' : '0',
        };
    }

    /**
     * Returns the dependencies for this event.
     */     
    protected get Depend() {
        return {
            place: { item: this.place as BaseObject, relation_info: { to: null, by: 'place' } }         // no relation to this in this[by -> 'place']
        };
    }

    /**
     * Returns the child dependencies of this event.
     */     
    protected get Children(){
        return [ /* empty */ ];
    }
}

/**************************************/
/* GUEST LOGIN/LOGOUT EVENT           */
/**************************************/

/**
 * @interface _GuestEventData
 * Structure for Guest Event-specific data.
 * `qrcode` holds the central QRSCAN.objid (registry key name kept for server contract).
 */
export interface _GuestEventData extends _Event {
    qrcode: number | null,
    login: boolean | null
}

/**
 * @class GuestEvent
 * Represents a guest login/logout event (registry l3/l4).
 */
export class GuestEvent extends RegistryEvent {
    protected _event: _GuestEventData = {
        created: null,
        target: null,
        qrcode: null,
        login: null,
    };

    constructor(data: dataService){
        super(data);

        this._event.created = new Date();
        this._event.target = 'GUEST';
    }

    /**
     * Returns event-specific information.
     */
    override get Info(){
        return this._event;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    /**
     * Determines whether the event is ready for notification.
     * Uses `_event.qrcode` (QRSCAN.objid) because AddRelationInfo clears it when the scan has no objid.
     */
    private get _isReady(){
        if (!this._event.qrcode){
            return false;
        }
        if (this.login === null){
            return false;
        }
        return true;
    }

    /**
     * Gets the QRSCAN associated with the guest login event.
     */
    get qrcode(): QrScan | null {
        if (this._children['qrcode'] instanceof QrScan){
            return this._children['qrcode'] as QrScan;
        }
        return null;
    }

    /**
     * Sets the QRSCAN associated with the guest login event.
     * Payload field name remains `qrcode` for `register_guest_event` on the server.
     */
    set qrcode(value: QrScan){
        if (this.SetChild('qrcode', value, 'qrcode')){
            this.ToNotify = this._isReady;
        }
    }

    /**
     * Gets the login status of the event.
     */
    get login(): boolean {
        return this._event.login || false;
    }

    /**
     * Sets the login status of the event.
     */
    set login(value: boolean) {
        if (this.patchValue(this._event, 'login', value)){
            this.ToNotify = this._isReady;
        }
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    /**
     * Returns the changes to be committed for the event.
     */
    protected get Change() {
        return {
            created: this._event.created ? GenericUtils.dateStrToMysql(this._event.created) : null,
            target: this._event.target,
            qrcode: this._event.qrcode,
            login: this._event.login ? '1' : '0',
        };
    }

    /**
     * Returns the dependencies for this event.
     */
    protected get Depend() {
        return {
            qrcode: { item: this.qrcode as BaseObject, relation_info: { to: null, by: 'qrcode' } }
        };
    }

    /**
     * Returns the child dependencies of this event.
     */
    protected get Children(){
        return [ /* empty */ ];
    }
}

/**************************************/
/* TICKET PRINTING EVENT              */
/**************************************/

/**
 * @interface _ReceiptEventData
 * Structure for Receipt Event-specific data.
 */
export interface _ReceiptEventData extends _Event {
    place: number | null,
    ticketinfo: string | null,
    charge: number | null
}

/**
 * @class ReceiptEvent
 * Represents a ticket printing event.
 */
export class ReceiptEvent extends RegistryEvent {
    protected _event: _ReceiptEventData = {
        created: null,
        target: null,
        place: null,
        ticketinfo: null,
        charge: null,
    };

    constructor(data: dataService){
        super(data);

        this._event.created = new Date();
        this._event.target = 'RECEIPT';
    }

    /**
     * Returns event-specific information.
     */
    override get Info(){
        return this._event;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    /**
     * Determines whether the event is ready for notification.
     * @returns `true` if all required fields are populated, otherwise `false`.
     */
    private get _isReady(): boolean {
        if (this._event.place === null){
            return false;
        }

        if (this._event.ticketinfo === null){
            return false;
        }

        return true;
    }

    /**
     * Gets the place associated with the receipt event.
     * @returns The `Place` object or `null` if not set.
     */
    get place(): Place | null {
        if (this._children['place'] instanceof Place){
            return this._children['place'] as Place;
        }
        return null;
    }

    /**
     * Sets the place associated with the receipt event.
     * Automatically updates the `ToNotify` flag if the event is ready.
     * @param value - The `Place` object to associate.
     */
    set place(value: Place){
        if (this.SetChild('place', value, 'place')){
            this.ToNotify = this._isReady;
        }
    }

    /**
     * Gets the ticket information associated with the receipt event.
     */
    get ticketinfo(): string | null {
        return this._event.ticketinfo;
    }

    /**
     * Sets the ticket information associated with the receipt event.
     * Automatically updates the `ToNotify` flag if the event is ready.
     * @param value - Ticket information payload.
     */
    set ticketinfo(value: string){
        if (this.patchValue(this._event, 'ticketinfo', value)){
            this.ToNotify = this._isReady;
        }
    }

    /**
     * Returns the charge amount extracted from the ticket information.
     * @returns Ticket total as number, or `null` if ticket info is missing.
     */
    get charge(): number | null {
        if (this._event.charge != null){
            return this._event.charge;
        }

        return null;
    }

    set charge(value: number | null){
        this.patchValue(this._event, 'charge', value);
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    /**
     * Returns the changes to be committed for the event.
     */
    protected get Change() {
        return {
            created: this._event.created ? GenericUtils.dateStrToMysql(this._event.created) : null,
            target: this._event.target,
            place: this._event.place,
            charge: this.charge,
            custom: this._event.ticketinfo
        };
    }

    /**
     * Returns the dependencies for this event.
     */
    protected get Depend() {
        return {
            place: { item: this.place as BaseObject, relation_info: { to: null, by: 'place' } }
        };
    }

    /**
     * Returns the child dependencies of this event.
     */
    protected get Children(){
        return [ /* empty */ ];
    }

}

/**
 * @enum LogActions
 * Enumerates the possible actions for logging events.
 */
export enum LogActions {
    DRAWER = 'DRAWER',
    LOGIN = 'LOGIN',
    GUEST = 'GUEST',
    RECEIPT = 'RECEIPT'
}

/**
 * @class eventsNotifier
 * Class for managing and logging different types of events.
 */
export class eventsNotifier {
    constructor(private data: dataService){
        // nothing to do
    }

    /**
     * Logs a specific action based on the type of event.
     * @param action - The action to log.
     * @param args - Arguments specific to the action type.
     */    
    LogAction(action: LogActions, args: any){
        switch(action){
            case LogActions.DRAWER:
                this._LogDrawerAction(args);
                return;
            case LogActions.LOGIN:
                this._LogLoginAction(args);
                return;
            case LogActions.GUEST:
                this._LogGuestAction(args);
                return;
            case LogActions.RECEIPT:
                this._LogReceiptAction(args);
                return;
        }
    }

    /*********************************/
    /* PRIVATE METHODS               */
    /*********************************/

    /**
     * Handles logging a drawer action.
     * @param args - Arguments for the drawer action.
     */    
    private _LogDrawerAction(args: any){
        if (!('printer' in args) || !args['printer']){
            console.error("[LOGGER] Required field 'printer' missing in arguments");
            return;
        }

        const _event = new DrawerEvent(this.data);
        if (_event){
            _event.printer = args['printer'];
            _event.Notify();
        }
    }

    /**
     * Handles logging a login/logout action.
     * @param args - Arguments for the login/logout action.
     */    
    private _LogLoginAction(args: any){
        if (!('place' in args) || !args['place']){
            console.error("[LOGGER] Required field 'place' missing in arguments");
            return;
        }

        const _place = args['place'];
        if (!_place.objid){
            console.error("[LOGGER] Place must have a valid objid for login event");
            return;
        }

        if (!('login' in args)){
            console.error("[LOGGER] Required field 'login' missing in arguments");
            return;
        }

        const _event = new LoginEvent(this.data);
        if (_event){
            _event.place = args['place'];
            _event.login = args['login'];
            _event.Notify();
        }
    }

    /**
     * Handles logging a guest login/logout action.
     * `args.table` must be the central QRSCAN (not the tenant QRCODE mesa).
     * @param args - Arguments for the guest action.
     */
    private _LogGuestAction(args: any){
        if (!('table' in args) || !args['table']){
            console.error("[LOGGER] Required field 'table' missing in arguments");
            return;
        }

        if (!('login' in args)){
            console.error("[LOGGER] Required field 'login' missing in arguments");
            return;
        }

        const _event = new GuestEvent(this.data);
        if (_event){
            _event.qrcode = args['table'];
            _event.login = args['login'];
            _event.Notify();
        }
    }

    /**
     * Handles logging a receipt print action.
     * @param args - Arguments for the receipt action.
     */
    private _LogReceiptAction(args: any){
        if (!('place' in args) || !args['place']){
            console.error("[LOGGER] Required field 'place' missing in arguments");
            return;
        }

        if (!('info' in args) || typeof args['info'] !== 'string' || String(args['info']).trim() === ''){
            console.error("[LOGGER] Required field 'info' missing in arguments");
            return;
        }

        const _event = new ReceiptEvent(this.data);
        _event.place = args['place'];
        _event.ticketinfo = args['info'];

        if ('charge' in args){
            const _charge = Number(args['charge']);
            _event.charge = isNaN(_charge) ? null : _charge;
        }
        
        _event.Notify();
    }
}